import gsap from 'gsap';
import { Container, Graphics, Text } from 'pixi.js';

import { config } from '../config/config';
import { Coordinates } from './declarations/PositionInfo';

const BULLET_NUMBER_TEXT_COLOR = '#FFFFFF';
const BULLET_NUMBER_BACKGROUND_COLOR = '#b022f3';
const BULLET_NUMBER_BACKGROUND_ALPHA = 1;
const BULLET_NUMBER_STROKE_COLOR = '#FFFFFF';

const BULLET_LABEL_TEXT_COLOR = '#FFFFFF';
const BULLET_LABEL_BACKGROUND_COLOR = '#b022f3';
const BULLET_LABEL_BACKGROUND_ALPHA = 1;
const BULLET_LABEL_STROKE_COLOR = '#FFFFFF';

const LABEL_TEXT_X_OFFSET = 25;
const LABEL_TEXT_X_PADDING = 15;
const LABEL_WIDTH = 400;
const LABEL_HEIGHT = 25;
const BULLET_RADIUS = 16;

const DOT_RADIUS = 10;
const DOT_COLOR = '#ebbfff';
const DOT_EXPANDED_COLOR = '#FFFFFF';

const SHOW_TWEEN_DUATION = 0.2;

export default class ItemBullet extends Container {
  private isLeft: boolean;
  private isHovered: boolean;
  private isShown: boolean;
  private shouldBeDisplayed: boolean;
  private labelContainer: Container;
  private bulletContainer: Container;
  private hideGoal: number;
  private bulletDot?: Graphics;

  constructor(position: Coordinates, title: string, dayLabel: string) {
    super();

    //console.log(`ItemBullet ${name}`, title, dayLabel, position);

    this.x = Math.round(position.x);
    this.y = Math.round(position.y);

    if (this.x > config.SIZES.SCENE_WIDTH / 2) {
      this.isLeft = true;
    } else {
      this.isLeft = false;
    }
    this.visible = false;
    this.isHovered = false;
    this.isShown = false;
    this.shouldBeDisplayed = false;

    this.labelContainer = new Container();
    this.bulletContainer = new Container();

    this.hideGoal = 0;

    this.createLabel(title);
    this.createBullet(dayLabel);

    this.eventMode = 'none';
    this.cursor = 'pointer';
  }

  setHoverState(doHover: boolean) {
    if (doHover === this.isHovered) {
      return;
    }
    this.isHovered = doHover;
    this.showBullet(doHover);
    this.showLabel(doHover);
    if (doHover) {
      this.bulletDot?.beginFill(DOT_EXPANDED_COLOR);
      this.bulletDot?.drawCircle(0, 0, DOT_RADIUS);
      this.bulletDot?.endFill();
    } else {
      this.bulletDot?.beginFill(DOT_COLOR);
      this.bulletDot?.drawCircle(0, 0, DOT_RADIUS);
      this.bulletDot?.endFill();
    }
  }

  displayBullet(display: boolean) {
    this.shouldBeDisplayed = display;
    this.showBullet(display);
  }

  showLabel(doShow: boolean) {
    gsap.killTweensOf(this.labelContainer);

    if (doShow) {
      this.labelContainer.visible = true;
    }

    gsap.to(this.labelContainer, {
      duration: SHOW_TWEEN_DUATION,
      x: doShow ? 0 : this.hideGoal,
      alpha: doShow ? 1.0 : 0,
      delay: doShow ? 0.1 : 0,
      ease: doShow ? 'sine.out' : 'sine.in',
      onComplete: () => {
        this.labelContainer.visible = doShow;
      },
    });
  }

  createLabel(text: string) {
    this.labelContainer.visible = false;

    const labelBackground = new Graphics();
    labelBackground.lineStyle(2, BULLET_LABEL_STROKE_COLOR);
    labelBackground.beginFill(BULLET_LABEL_BACKGROUND_COLOR, BULLET_LABEL_BACKGROUND_ALPHA);
    labelBackground.drawRoundedRect(0, 0, LABEL_WIDTH, LABEL_HEIGHT, 100);
    labelBackground.endFill();
    labelBackground.y = -LABEL_HEIGHT / 2;
    this.labelContainer.addChild(labelBackground);

    const labelText = new Text('', {
      fontFamily: config.FONT_BASE,
      fontSize: '15px',
      fill: BULLET_LABEL_TEXT_COLOR,
    });
    labelText.anchor.y = 0.5;
    if (this.isLeft) {
      labelText.x = -LABEL_TEXT_X_OFFSET;
      labelText.anchor.x = 1.0;
    } else {
      labelText.x = LABEL_TEXT_X_OFFSET;
    }
    this.labelContainer.addChild(labelText);

    const labelMask = new Graphics();
    labelMask.beginFill(0x000000, 1.0);
    if (this.isLeft) {
      labelMask.drawRect(-LABEL_WIDTH, -(LABEL_HEIGHT + 4) / 2, LABEL_WIDTH, LABEL_HEIGHT + 4);
    } else {
      labelMask.drawRect(0, -(LABEL_HEIGHT + 4) / 2, LABEL_WIDTH, LABEL_HEIGHT + 4);
    }
    labelMask.endFill();
    this.addChild(labelMask);
    this.labelContainer.mask = labelMask;

    labelText.text = text;
    labelText.updateText(true);

    if (this.isLeft) {
      labelBackground.x = -labelText.width - LABEL_TEXT_X_OFFSET - LABEL_TEXT_X_PADDING;
    } else {
      labelBackground.x = -LABEL_WIDTH + labelText.width + LABEL_TEXT_X_OFFSET + LABEL_TEXT_X_PADDING;
    }

    this.hideGoal = labelText.width + LABEL_TEXT_X_OFFSET + LABEL_TEXT_X_PADDING;
    if (!this.isLeft) {
      this.hideGoal *= -1;
    }

    this.labelContainer.x = this.hideGoal;
    this.addChild(this.labelContainer);
  }

  createBullet(text: string) {
    const bullet = new Graphics();
    bullet.lineStyle(2, BULLET_NUMBER_STROKE_COLOR);
    bullet.beginFill(BULLET_NUMBER_BACKGROUND_COLOR, BULLET_NUMBER_BACKGROUND_ALPHA);
    bullet.drawCircle(0, 0, BULLET_RADIUS);
    bullet.endFill();
    this.bulletContainer.addChild(bullet);

    if (parseInt(text) > 0) {
      const bulletNumberText = new Text(text, {
        fontFamily: config.FONT_BASE,
        fontSize: '17px',
        fill: BULLET_NUMBER_TEXT_COLOR,
      });
      bulletNumberText.anchor.x = 0.5;
      bulletNumberText.anchor.y = 0.5;
      this.bulletContainer.addChild(bulletNumberText);
    } else {
      this.bulletDot = new Graphics();
      this.bulletDot.beginFill(DOT_COLOR);
      this.bulletDot.drawCircle(0, 0, DOT_RADIUS);
      this.bulletDot.endFill();
      this.bulletContainer.addChild(this.bulletDot);
    }

    this.addChild(this.bulletContainer);
  }

  showBullet(doShow: boolean) {
    if (doShow === this.isShown) {
      return;
    }

    gsap.killTweensOf(this.bulletContainer);
    gsap.killTweensOf(this.bulletContainer.scale);

    if (doShow) {
      this.isShown = true;
      this.visible = true;

      this.bulletContainer.alpha = 0.0;
      gsap.to(this.bulletContainer, {
        duration: 0.3,
        alpha: 1.0,
      });

      this.bulletContainer.scale.x = 0.4;
      this.bulletContainer.scale.y = 0.4;
      gsap.to(this.bulletContainer.scale, {
        duration: 0.3,
        x: 1.0,
        y: 1.0,
        ease: 'back.out',
      });
    } else if (!this.shouldBeDisplayed) {
      this.isShown = false;
      gsap.to(this.bulletContainer, {
        duration: 0.2,
        alpha: 0.0,
        delay: 0.1,
      });
      gsap.to(this.bulletContainer.scale, {
        duration: 0.2,
        x: 0.2,
        y: 0.2,
        delay: 0.1,
        ease: 'sine.in',
        onComplete: () => {
          this.visible = false;
        },
      });
    }
  }
}
